import { useCallback, useEffect, useRef, useState } from 'react'
import type { Editor } from '@tiptap/react'
import { toast } from 'sonner'
import { uploadFileWithProgress } from '@/api/uploadFile'
import { useAppSelector } from '@/store/hooks'
import { FIELD_LIMITS } from '@/features/editor/constants/fieldLimits'
import { uploadErrorText } from '@/api/uploadError'
import { messagesNow } from '@/i18n'
import { messages } from './messages'
import { setUploadPlaceholder, uploadPlaceholderPos } from './uploadPlaceholder'

interface Result {
  fileInputRef: React.RefObject<HTMLInputElement>
  uploading: boolean
  /** Процент загрузки картинки (`null` — не грузим). */
  progress: number | null
  requestUpload: () => void
  /** Выбрать файл на замену выделенной картинки. */
  requestReplace: () => void
  /** `replace` — заменить выделенную картинку; вставка из буфера зовёт без него. */
  uploadImage: (file: File, replace?: boolean) => void
  handleFile: (e: React.ChangeEvent<HTMLInputElement>) => void
}

/** Поставить картинку туда, где показывалась загрузка. Замена меняет адрес у той же картинки. */
const placeImage = (editor: Editor, pos: number, src: string, replacing: boolean) => {
  const node = replacing ? editor.state.doc.nodeAt(pos) : null
  if (node?.type.name === 'image') {
    editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, src }))
    return
  }
  // Без `focus()` и без переноса выделения: автор мог уйти печатать в другое место.
  editor
    .chain()
    .insertContentAt(pos, { type: 'image', attrs: { src } }, { updateSelection: false })
    .run()
}

export const useImageUpload = (editor: Editor | null): Result => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [progress, setProgress] = useState<number | null>(null)
  const uploading = progress !== null
  const replacing = useRef(false)
  const accessToken = useAppSelector((s) => s.authTokens.accessToken || s.dev.pastedJwt)

  // Диалог выбора файла закрыт без выбора: режим замены не должен остаться взведённым.
  // React-типы `onCancel` у <input> не знают, поэтому слушатель нативный.
  useEffect(() => {
    const input = fileInputRef.current
    if (!input) return
    const reset = () => {
      replacing.current = false
    }
    input.addEventListener('cancel', reset)
    return () => input.removeEventListener('cancel', reset)
  }, [])

  const requestUpload = useCallback(() => {
    if (uploading) return
    replacing.current = false
    fileInputRef.current?.click()
  }, [uploading])

  const requestReplace = useCallback(() => {
    if (uploading) return
    replacing.current = true
    fileInputRef.current?.click()
  }, [uploading])

  const uploadImage = (file: File, replace = false) => {
    if (!editor) return
    if (uploading) {
      toast.error(messagesNow(messages).imageUploadBusy)
      return
    }
    if (!file.type.startsWith('image/')) {
      toast.error(messagesNow(messages).notAnImage)
      return
    }
    if (file.size > FIELD_LIMITS.UPLOAD_MAX_BYTES) {
      toast.error(messagesNow(messages).fileTooLarge(FIELD_LIMITS.UPLOAD_MAX_MB))
      return
    }
    setProgress(0)
    setUploadPlaceholder(editor, editor.state.selection.from)
    void uploadFileWithProgress({
      file,
      compressionType: 'banner_compression',
      accessToken,
      onProgress: (p) => setProgress(p.percent),
    })
      .then((url) => {
        if (editor.isDestroyed) return
        const pos = uploadPlaceholderPos(editor)
        if (pos !== null) placeImage(editor, pos, url, replace)
      })
      .catch((err: unknown) => {
        if (!editor.isDestroyed) toast.error(uploadErrorText(err))
      })
      .finally(() => {
        if (!editor.isDestroyed) setUploadPlaceholder(editor, null)
        setProgress(null)
      })
  }

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    const replace = replacing.current
    replacing.current = false
    if (file) uploadImage(file, replace)
  }

  return {
    fileInputRef,
    uploading,
    progress,
    requestUpload,
    requestReplace,
    uploadImage,
    handleFile,
  }
}
